import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { LearningDetailService } from '@shared/services/api/learning-detail.service';
import { Learning } from '@shared/entities/learning';
import { ProfileService } from '@shared/services/api/profile.service';
import { Roles } from '@shared/models/types';
import { LearningAttachmentService } from '@shared/services/api/learning-attachment.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { Attachment } from '@shared/entities/attachment';
import { ModalService } from '@shared/services/modal.service';
import { LearningImageModalComponent } from '../learning-image-modal/learning-image-modal.component';
import { LearningVideoModalComponent } from '../learning-video-modal/learning-video-modal.component';
import { Subject, takeUntil, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-learning-detail',
  templateUrl: './learning-detail.component.html',
  styleUrls: ['./learning-detail.component.scss']
})
export class LearningDetailComponent implements OnInit, OnDestroy {
  _learningId = '';
  isEdit = false;
  isLight = true;
  isLoading = false;
  isDeleting = false;
  backendHtml = '';
  learning: Learning | null = null;
  isFavorite = false;
  isAdmin = false;
  isTogglingFavorite = false;

  videoAttachments: Attachment[] = [];
  isLoadingVideos = false;

  imageAttachments: Attachment[] = [];
  isLoadingImages = false;

  documentAttachments: Attachment[] = [];
  isLoadingDocuments = false;

  selectedAttachmentTab = 0;
  private _destroy$ = new Subject<void>();

  constructor(
    private _route: ActivatedRoute,
    private _location: Location,
    private _learningDetailService: LearningDetailService,
    private _profileService: ProfileService,
    private _learningAttachmentService: LearningAttachmentService,
    private _fsAttachmentService: FsAttachmentService,
    private _cdr: ChangeDetectorRef,
    private _router: Router,
    private _modalService: ModalService,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.determineInitialTab();

    this._profileService.role$.pipe(takeUntil(this._destroy$)).subscribe((role) => {
      this.isAdmin = role === Roles.ADMIN;
    });

    this._route.params.pipe(takeUntil(this._destroy$)).subscribe((params: any) => {
      this._learningId = params['id'] || '';
      this.isEdit = !!this._learningId;
      if (this._learningId) {
        this.loadLearning(this._learningId);
        this.loadVideoAttachments(this._learningId);
        this.loadImageAttachments(this._learningId);
        this.loadDocumentAttachments(this._learningId);
      }
    });
  }

  private loadLearning(id: string): void {
    this.isLoading = true;
    this._learningDetailService.findLearning(id).subscribe((learning) => {
      this.isLoading = false;
      if (learning) {
        this.applyLearning(learning);
      } else {
        this.isLoading = true;
        this._learningDetailService.findByFilter(id).subscribe((l) => {
          this.isLoading = false;
          if (l) {
            this.applyLearning(l);
          }
        });
      }
    });
  }

  private applyLearning(learning: Learning) {
    this.learning = learning;
    this.backendHtml = learning.content ? String((learning as any).content) : '';
    this.isEdit = true;
    this.isFavorite = !!learning.isFavorite;
  }

  private loadVideoAttachments(learningId: string): void {
    this.isLoadingVideos = true;

    this._learningAttachmentService.getAttachment(learningId, 'video').subscribe({
      next: (learningAttachments) => {
        if (!learningAttachments || learningAttachments.length === 0) {
          this.isLoadingVideos = false;
          this.videoAttachments = [];
          return;
        }

        const allAttachments: Attachment[] = learningAttachments.map((item) => item as any as Attachment);

        const videoAttachments = allAttachments.filter((attachment) => {
          return attachment.contentType?.startsWith('video/');
        });

        this.videoAttachments = [];
        this.loadVideoBlobsSequentially(videoAttachments, 0);
      },
      error: () => {
        this.isLoadingVideos = false;
        this.videoAttachments = [];
      }
    });
  }

  private loadVideoBlobsSequentially(videoAttachments: Attachment[], index: number): void {
    if (index >= videoAttachments.length) {
      this.isLoadingVideos = false;
      this.updateInitialTab();
      this._cdr.detectChanges();
      return;
    }

    const attachment = videoAttachments[index];
    const attachmentId = attachment.attachmentId || attachment.id || '';

    this._fsAttachmentService.getAttachmentUrl(attachmentId).subscribe({
      next: (blob) => {
        const blobUrl = window.URL.createObjectURL(blob);

        this.videoAttachments.push({
          ...attachment,
          filePath: blobUrl
        });

        this.loadVideoBlobsSequentially(videoAttachments, index + 1);
      },
      error: () => {
        this.videoAttachments.push({
          ...attachment,
          filePath: undefined
        });
        this.loadVideoBlobsSequentially(videoAttachments, index + 1);
      }
    });
  }

  private loadImageAttachments(learningId: string): void {
    this.isLoadingImages = true;

    this._learningAttachmentService.getAttachment(learningId, 'image').subscribe({
      next: (learningAttachments) => {
        if (!learningAttachments || learningAttachments.length === 0) {
          this.isLoadingImages = false;
          this.imageAttachments = [];
          return;
        }

        const allAttachments: Attachment[] = learningAttachments.map((item) => item as any as Attachment);

        const imageAttachments = allAttachments.filter((attachment) => {
          return attachment.contentType?.startsWith('image/');
        });

        if (imageAttachments.length === 0) {
          this.isLoadingImages = false;
          this.imageAttachments = [];
          return;
        }

        this.imageAttachments = [];
        this.loadImageBlobsInParallel(imageAttachments);
      },
      error: () => {
        this.isLoadingImages = false;
        this.imageAttachments = [];
      }
    });
  }

  private loadImageBlobsInParallel(imageAttachments: Attachment[]): void {
    const requests = imageAttachments.map((attachment) => {
      const attachmentId = attachment.attachmentId || attachment.id || '';
      return this._fsAttachmentService.getAttachmentUrl(attachmentId).pipe(
        map((blob) => ({
          ...attachment,
          filePath: window.URL.createObjectURL(blob)
        })),
        catchError(() => of({ ...attachment, filePath: undefined }))
      );
    });

    forkJoin(requests).subscribe({
      next: (results) => {
        this.imageAttachments = results as Attachment[];
        this.isLoadingImages = false;
        this.updateInitialTab();
        this._cdr.detectChanges();
      },
      error: () => {
        this.isLoadingImages = false;
        this._cdr.detectChanges();
      }
    });
  }

  private loadDocumentAttachments(learningId: string): void {
    this.isLoadingDocuments = true;

    this._learningAttachmentService.getAttachment(learningId, 'document').subscribe({
      next: (learningAttachments) => {
        if (!learningAttachments || learningAttachments.length === 0) {
          this.isLoadingDocuments = false;
          this.documentAttachments = [];
          return;
        }

        const allAttachments: Attachment[] = learningAttachments.map((item) => item as any as Attachment);

        const documentAttachments = allAttachments.filter((attachment) => {
          return (
            attachment.contentType === 'application/pdf' ||
            attachment.contentType === 'application/msword' ||
            attachment.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          );
        });

        if (documentAttachments.length === 0) {
          this.isLoadingDocuments = false;
          this.documentAttachments = [];
          return;
        }

        this.documentAttachments = documentAttachments;
        this.isLoadingDocuments = false;
        this.updateInitialTab();
      },
      error: () => {
        this.isLoadingDocuments = false;
        this.documentAttachments = [];
        this.updateInitialTab();
      }
    });
  }

  downloadDocument(attachmentId: string | undefined, fileName: string | undefined): void {
    if (!attachmentId) {
      return;
    }

    this._fsAttachmentService.downloadFile(attachmentId, fileName).subscribe({
      next: () => {
        // Implementation
      },
      error: () => {
        // Implementation
      }
    });
  }

  getYear() {
    return new Date().getFullYear();
  }

  toggleLearningActivation(): void {
    this.isLoading = true;
    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  isLearningActive(): boolean {
    return true;
  }

  getToggleButtonText(): string {
    return this.isLearningActive() ? 'LEARNING_FORM.DEACTIVATE_BUTTON' : 'LEARNING_FORM.ACTIVATE_BUTTON';
  }

  goToForm(): void {
    this._router.navigate(['/learning', this._learningId, 'edit']);
  }

  goBack(): void {
    this._location.back();
  }

  goToFeedbackForm(): void {
    this._router.navigate(['/learning', this._learningId, 'feedback']);
  }

  onDelete(): void {
    if (!this._learningId) {
      return;
    }
    const confirmed = window.confirm(this.translateService.instant('LEARNING_FORM.CONFIRM_DELETE_LEARNING'));
    if (!confirmed) {
      return;
    }

    this.isDeleting = true;
    this._learningDetailService.deleteLearning(this._learningId).subscribe({
      next: (_res) => {
        this.isDeleting = false;
        if (_res === true) {
          this._router.navigate(['/learning']);
        }
      },
      error: () => {
        this.isDeleting = false;
      }
    });
  }

  determineInitialTab(): void {
    this.selectedAttachmentTab = 0;
  }

  updateInitialTab(): void {
    if (!this.isLoadingVideos && !this.isLoadingImages && !this.isLoadingDocuments) {
      if (this.videoAttachments.length > 0) {
        this.selectedAttachmentTab = 0;
      } else if (this.imageAttachments.length > 0) {
        this.selectedAttachmentTab = 1;
      } else if (this.documentAttachments.length > 0) {
        this.selectedAttachmentTab = 2;
      }
      this._cdr.detectChanges();
    }
  }

  get showAttachmentTabs(): boolean {
    const available = [
      !this.isLoadingVideos && this.videoAttachments.length > 0,
      !this.isLoadingImages && this.imageAttachments.length > 0,
      !this.isLoadingDocuments && this.documentAttachments.length > 0
    ];
    return available.filter(Boolean).length > 1;
  }

  switchAttachmentTab(tabIndex: number): void {
    if (this.selectedAttachmentTab === tabIndex) {
      return;
    }
    this.selectedAttachmentTab = tabIndex;
    this._cdr.detectChanges();
  }

  openImageFullscreen(imageUrl?: string | null): void {
    if (!imageUrl) return;
    this._modalService.open(
      LearningImageModalComponent,
      { imageUrl, imageAlt: 'Image', showClose: true },
      { centered: true }
    );
  }

  openVideoFullscreen(videoUrl?: string | null, videoTitle?: string | null): void {
    if (!videoUrl) return;

    try {
      const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[];
      videos.forEach((v) => {
        try {
          v.pause();
        } catch {
          // Ignore
        }
      });
    } catch {
      // Ignore
    }

    this._modalService.open(
      LearningVideoModalComponent,
      { videoUrl, videoTitle, showClose: true },
      { centered: true, size: 'xl' }
    );
  }

  ngOnDestroy(): void {
    this.videoAttachments.forEach((video) => {
      if (video.filePath && video.filePath.startsWith('blob:')) {
        window.URL.revokeObjectURL(video.filePath);
      }
    });

    this.imageAttachments.forEach((image) => {
      if (image.filePath && image.filePath.startsWith('blob:')) {
        window.URL.revokeObjectURL(image.filePath);
      }
    });

    this._destroy$.next();
    this._destroy$.complete();
  }

  getFormattedTags(learning: Learning): string {
    if (!learning.tags || learning.tags.length === 0) return '';
    return learning.tags.map((tag) => `#${tag}`).join(' | ');
  }

  toggleFavoriteAction(): void {
    if (!this._learningId) return;

    this.isTogglingFavorite = true;

    this._learningDetailService.toggleFavorite(this._learningId).subscribe({
      next: (res) => {
        if (res === 'OK') {
          this._learningDetailService.findLearning(this._learningId).subscribe({
            next: (learning) => {
              this.isTogglingFavorite = false;
              if (learning) {
                this.applyLearning(learning);
                this.isFavorite = !!learning.isFavorite;
              } else {
                this.isFavorite = !this.isFavorite;
              }
            },
            error: () => {
              this.isTogglingFavorite = false;
              this.isFavorite = !this.isFavorite;
            }
          });
        } else {
          this.isTogglingFavorite = false;
        }
      },
      error: () => {
        this.isTogglingFavorite = false;
      }
    });
  }
}
